import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, doc, updateDoc, query, orderBy, where, Timestamp, addDoc } from 'firebase/firestore';
import { db } from './firebase';
import { useNavigate } from 'react-router-dom';
import './ManageDiscountCodes.css';

interface NewDiscountCode {
  code: string;
  discountType: 'percentage' | 'fixed_amount';
  discountValue: number;
  maxUses: number;
  expirationDate: string;
}

interface DiscountCode {
  id: string;
  code: string;
  discountType: 'percentage' | 'fixed_amount';
  discountValue: number;
  maxUses: number;
  usesCount: number;
  expirationDate: Timestamp;
  applicableServices?: string[];
  createdAt: Timestamp;
  active: 'active' | 'inactive';
}

interface Subcategory {
  id: string;
  name: string;
  categoryId: string;
  icon: string;
  order?: number;
  commissionRate?: number;
}

interface DiscountStats {
  totalCodes: number;
  activeCodes: number;
  inactiveCodes: number;
  totalUses: number;
}

const ManageDiscountCodes: React.FC = () => {
  const navigate = useNavigate();
  const [discountCodes, setDiscountCodes] = useState<DiscountCode[]>([]);
  const [filteredDiscountCodes, setFilteredDiscountCodes] = useState<DiscountCode[]>([]);
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filter states
  const [codeFilter, setCodeFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  // Inline editing states
  const [editingCode, setEditingCode] = useState<string | null>(null);
  // Sửa type cho editingField
  const [editingField, setEditingField] = useState<'code' | 'discountValue' | 'maxUses' | 'active' | 'expirationDate' | null>(null);
  const [editingValue, setEditingValue] = useState<string>('');
  const [updatingField, setUpdatingField] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 50;
  
  // State for adding new discount
  const [isAddingDiscount, setIsAddingDiscount] = useState(false);
  const [addingDiscount, setAddingDiscount] = useState(false);
  const [newDiscount, setNewDiscount] = useState<NewDiscountCode>({
    code: '',
    discountType: 'percentage',
    discountValue: 0,
    maxUses: 0,
    expirationDate: ''
  });

  const resetNewDiscount = () => {
    setNewDiscount({
      code: '',
      discountType: 'percentage',
      discountValue: 0,
      maxUses: 0,
      expirationDate: ''
    });
  };

  const handleNewDiscountKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      saveNewDiscount();
    } else if (e.key === 'Escape') {
      cancelAddingDiscount();
    }
  };

  const startAddingDiscount = () => {
    setIsAddingDiscount(true);
  };

  const cancelAddingDiscount = () => {
    setIsAddingDiscount(false);
    resetNewDiscount();
  };

  const saveNewDiscount = async () => {
    if (addingDiscount) return;

    // Validate input
    if (!newDiscount.code.trim()) {
      alert('Mã khuyến mãi không thể để trống');
      return;
    }

    if (newDiscount.discountValue < 0) {
      alert('Giá trị giảm không hợp lệ');
      return;
    }

    if (newDiscount.maxUses < 0) {
      alert('Số lần sử dụng không hợp lệ');
      return;
    }

    if (!newDiscount.expirationDate) {
      alert('Ngày hết hạn không thể để trống');
      return;
    }

    try {
      setAddingDiscount(true);

      const expirationDate = new Date(newDiscount.expirationDate);
      if (isNaN(expirationDate.getTime())) {
        alert('Ngày hết hạn không hợp lệ');
        return;
      }

      const docRef = await addDoc(collection(db, 'discount_codes'), {
        code: newDiscount.code.trim(),
        discountType: newDiscount.discountType,
        discountValue: newDiscount.discountValue,
        maxUses: newDiscount.maxUses,
        usesCount: 0,
        expirationDate: Timestamp.fromDate(expirationDate),
        applicableServices: [],
        createdAt: Timestamp.now(),
        active: 'active'
      });

      const newDiscountCode: DiscountCode = {
        id: docRef.id,
        code: newDiscount.code.trim(),
        discountType: newDiscount.discountType,
        discountValue: newDiscount.discountValue,
        maxUses: newDiscount.maxUses,
        usesCount: 0,
        expirationDate: Timestamp.fromDate(expirationDate),
        applicableServices: [],
        createdAt: Timestamp.now(),
        active: 'active'
      };

      setDiscountCodes([newDiscountCode, ...discountCodes]);
      setFilteredDiscountCodes([newDiscountCode, ...filteredDiscountCodes]);
      
      setIsAddingDiscount(false);
      resetNewDiscount();

    } catch (error: any) {
      console.error('Lỗi khi thêm mã khuyến mãi:', error);
      alert('Không thể thêm mã khuyến mãi. Vui lòng thử lại.');
    } finally {
      setAddingDiscount(false);
    }
  };

  const fetchSubcategories = async () => {
    try {
      console.log('Bắt đầu tải danh sách dịch vụ...');

      let subcategoriesQuery;
      let querySnapshot;

      try {
        subcategoriesQuery = query(
          collection(db, 'subcategories'),
          orderBy('order', 'asc')
        );
        console.log('Thử query subcategories với orderBy order...');
        querySnapshot = await getDocs(subcategoriesQuery);
      } catch (indexError) {
        console.warn('Query subcategories với orderBy thất bại, thử query đơn giản:', indexError);
        subcategoriesQuery = collection(db, 'subcategories');
        console.log('Thử query subcategories đơn giản...');
        querySnapshot = await getDocs(subcategoriesQuery);
      }

      const subcategoriesData: Subcategory[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        subcategoriesData.push({
          id: doc.id,
          name: data.name || '',
          categoryId: data.categoryId || '',
          icon: data.icon || '',
          order: data.order || 0,
          commissionRate: data.commissionRate || 0
        });
      });

      if (subcategoriesData.length > 0) {
        subcategoriesData.sort((a, b) => {
          const aOrder = a.order || 0;
          const bOrder = b.order || 0;
          return aOrder - bOrder;
        });
      }

      console.log(`Đã tải ${subcategoriesData.length} dịch vụ`);
      setSubcategories(subcategoriesData);
      
    } catch (error: any) {
      console.error('Lỗi khi tải danh sách dịch vụ:', error);
    }
  };

  const fetchDiscountCodes = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Bắt đầu tải danh sách khuyến mãi...');

      let discountQuery;
      let querySnapshot;

      try {
        const constraints: any[] = [];
        if (statusFilter) constraints.push(where('active', '==', statusFilter));
        if (typeFilter) constraints.push(where('discountType', '==', typeFilter));
        try {
          discountQuery = query(
            collection(db, 'discount_codes'),
            ...constraints,
            orderBy('createdAt', 'desc')
          );
          console.log('Thử query với constraints + orderBy createdAt...');
          querySnapshot = await getDocs(discountQuery);
        } catch (indexError) {
          console.warn('Query với orderBy thất bại, thử query không order:', indexError);
          discountQuery = query(
            collection(db, 'discount_codes'),
            ...constraints
          );
          console.log('Thử query không order...');
          querySnapshot = await getDocs(discountQuery);
        }
      } catch (qErr) {
        console.warn('Xây dựng query có điều kiện thất bại, fallback lấy tất cả:', qErr);
        discountQuery = collection(db, 'discount_codes');
        querySnapshot = await getDocs(discountQuery);
      }

      const discountCodesData: DiscountCode[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        console.log('Discount code doc:', doc.id, data);
        
        discountCodesData.push({
          id: doc.id,
          code: data.code || '',
          discountType: data.discountType || 'percentage',
          discountValue: data.discountValue || 0,
          maxUses: data.maxUses || 0,
          usesCount: data.usesCount || 0,
          expirationDate: data.expirationDate,
          applicableServices: data.applicableServices || [],
          createdAt: data.createdAt,
          active: data.active || 'active'
        });
      });

      console.log(`Đã tải ${discountCodesData.length} mã khuyến mãi`);
      setDiscountCodes(discountCodesData);
      setFilteredDiscountCodes(discountCodesData);
      
    } catch (error: any) {
      console.error('Lỗi chi tiết khi tải danh sách khuyến mãi:', error);
      
      let errorMessage = 'Không thể tải danh sách khuyến mãi';
      
      if (error.code === 'permission-denied') {
        errorMessage = 'Không có quyền truy cập dữ liệu. Vui lòng kiểm tra quyền đăng nhập.';
      } else if (error.code === 'unavailable') {
        errorMessage = 'Dịch vụ hiện không khả dụng. Vui lòng thử lại sau.';
      } else if (error.code === 'failed-precondition') {
        errorMessage = 'Cần tạo index cho database. Vui lòng liên hệ quản trị viên.';
      } else if (error.message) {
        errorMessage = `Lỗi: ${error.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = (discountCodesToFilter: DiscountCode[]) => {
    let filtered = [...discountCodesToFilter];

    // Filter by code
    if (codeFilter.trim()) {
      filtered = filtered.filter(code =>
        code.code.toLowerCase().includes(codeFilter.toLowerCase())
      );
    }

    // Filter by discount type
    if (typeFilter) {
      filtered = filtered.filter(code => code.discountType === typeFilter);
    }

    // Filter by status (active/inactive)
    if (statusFilter) {
      filtered = filtered.filter(code => code.active === statusFilter);
    }

    return filtered;
  };

  const calculateStats = (codes: DiscountCode[]): DiscountStats => {
    const stats: DiscountStats = {
      totalCodes: codes.length,
      activeCodes: 0,
      inactiveCodes: 0,
      totalUses: 0
    };

    codes.forEach(code => {
      // Count total uses
      stats.totalUses += code.usesCount;
      
      // Check if active or inactive
      if (code.active === 'active') {
        stats.activeCodes++;
      } else {
        stats.inactiveCodes++;
      }
    });

    return stats;
  };

  const startEditing = (discountCode: DiscountCode, field: 'code' | 'discountValue' | 'maxUses' | 'active' | 'expirationDate') => {
    setEditingCode(discountCode.id);
    setEditingField(field);
    
    if (field === 'code') {
      setEditingValue(discountCode.code);
    } else if (field === 'discountValue') {
      setEditingValue(discountCode.discountValue.toString());
    } else if (field === 'maxUses') {
      setEditingValue(discountCode.maxUses.toString());
    } else if (field === 'active') {
      setEditingValue(discountCode.active);
    } else if (field === 'expirationDate') {
      setEditingValue(timestampToInputValue(discountCode.expirationDate));
    }
  };

  const cancelEditing = () => {
    setEditingCode(null);
    setEditingField(null);
    setEditingValue('');
  };

  const saveDiscountCode = async (codeId: string) => {
    if (!editingField) return;
    
    if (editingField === 'code' && !editingValue.trim()) {
      alert('Mã khuyến mãi không thể để trống');
      return;
    }

    if ((editingField === 'discountValue' || editingField === 'maxUses')) {
      const numValue = parseFloat(editingValue);
      if (isNaN(numValue) || numValue < 0) {
        alert(`${editingField === 'discountValue' ? 'Giá trị giảm giá' : 'Số lần sử dụng tối đa'} phải là số dương`);
        return;
      }
    }

    if (editingField === 'active' && !['active', 'inactive'].includes(editingValue)) {
      alert('Trạng thái phải là "active" hoặc "inactive"');
      return;
    }

    if (editingField === 'expirationDate') {
      const dateValue = new Date(editingValue);
      if (isNaN(dateValue.getTime())) {
        alert('Ngày hết hạn không hợp lệ');
        return;
      }
    }

    try {
      setUpdatingField(codeId);
      const codeRef = doc(db, 'discount_codes', codeId);
      
      const updateData: any = {};
      if (editingField === 'code') {
        updateData[editingField] = editingValue.trim().toUpperCase();
      } else if (editingField === 'active') {
        updateData[editingField] = editingValue;
      } else if (editingField === 'expirationDate') {
        updateData[editingField] = Timestamp.fromDate(new Date(editingValue));
      } else {
        updateData[editingField] = parseFloat(editingValue);
      }
      
      await updateDoc(codeRef, updateData);

      // Update local state
      const updatedCodes = discountCodes.map(code =>
        code.id === codeId
          ? { 
              ...code, 
              [editingField]: editingField === 'code' 
                ? editingValue.trim().toUpperCase()
                : editingField === 'active'
                ? editingValue
                : editingField === 'expirationDate'
                ? Timestamp.fromDate(new Date(editingValue))
                : parseFloat(editingValue)
            }
          : code
      );
      
      setDiscountCodes(updatedCodes);

      // Apply filters to updated data
      const filtered = applyFilters(updatedCodes);
      setFilteredDiscountCodes(filtered);

      // Reset editing state
      setEditingCode(null);
      setEditingField(null);
      setEditingValue('');

    } catch (error) {
      console.error('Lỗi khi cập nhật:', error);
      setError('Cập nhật thất bại');
    } finally {
      setUpdatingField(null);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent, codeId: string) => {
    if (e.key === 'Enter') {
      saveDiscountCode(codeId);
    } else if (e.key === 'Escape') {
      cancelEditing();
    }
  };

  const formatDate = (timestamp: Timestamp): string => {
    if (!timestamp) return 'N/A';
    return timestamp.toDate().toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDiscountValue = (value: number, type: string): string => {
    if (type === 'percentage') {
      return `${value}%`;
    } else {
      return new Intl.NumberFormat('vi-VN', {
        style: 'currency',
        currency: 'VND'
      }).format(value);
    }
  };

  const getSubcategoryNames = (serviceIds: string[]): string => {
    if (!serviceIds || serviceIds.length === 0) return 'Tất cả dịch vụ';
    
    const names = serviceIds
      .map(id => {
        const subcategory = subcategories.find(sub => sub.id === id);
        return subcategory ? subcategory.name : `Unknown (${id})`;
      })
      .filter(name => !name.startsWith('Unknown'))
      .join(', ');
    
    return names || 'Không có dịch vụ';
  };

  const handleDiscountCodeClick = (discountId: string) => {
    navigate(`/admin/manage-discount-codes/${discountId}`);
  };

  // Sử dụng hàm chuyển Timestamp sang ISO string
  function timestampToInputValue(ts: Timestamp): string {
    if (!ts) return '';
    const date = ts.toDate();
    // Định dạng yyyy-MM-ddTHH:mm cho input type datetime-local
    return date.toISOString().slice(0, 16);
  }


  useEffect(() => {
    const fetchData = async () => {
      await fetchSubcategories();
      await fetchDiscountCodes();
    };
    fetchData();
  }, []);

  // Refetch when server-side filters change
  useEffect(() => {
    setCurrentPage(1);
    fetchDiscountCodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter, statusFilter]);

  // Memoized filters and pagination
  const memoFilteredDiscountCodes = useMemo(() => applyFilters(discountCodes), [discountCodes, codeFilter, typeFilter, statusFilter]);

  const totalPages = useMemo(() => Math.ceil(memoFilteredDiscountCodes.length / recordsPerPage) || 1, [memoFilteredDiscountCodes.length]);
  const displayedDiscountCodes = useMemo(() => {
    const start = (currentPage - 1) * recordsPerPage;
    return memoFilteredDiscountCodes.slice(start, start + recordsPerPage);
  }, [memoFilteredDiscountCodes, currentPage]);

  useEffect(() => {
    setFilteredDiscountCodes(memoFilteredDiscountCodes);
    if (currentPage > totalPages) setCurrentPage(1);
  }, [memoFilteredDiscountCodes, currentPage, totalPages]);

  if (loading) {
    return (
      <div className="manage-discount-codes">
        <div className="page-header">
          <h2>Quản lý khuyến mãi</h2>
          <p className="page-subtitle">Quản lý và theo dõi tất cả mã khuyến mãi</p>
        </div>
        <div className="dashboard-loading">
          <div className="loading-spinner">Đang tải dữ liệu...</div>
        </div>
      </div>
    );
  }

  const stats = calculateStats(discountCodes);

  return (
    <div className="manage-discount-codes">
      <div className="page-header">
        <h2>Quản lý khuyến mãi</h2>
        <p className="page-subtitle">Quản lý và theo dõi tất cả mã khuyến mãi</p>
        <p className="edit-instruction">💡 Click vào các trường hoặc icon ✏️ để chỉnh sửa. Nhấn Enter hoặc click ra ngoài để lưu, Escape để hủy.</p>
      </div>

      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card total">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
            <div className="stat-value">{stats.totalCodes}</div>
            <div className="stat-label">Tổng mã khuyến mãi</div>
          </div>
        </div>
        
        <div className="stat-card active">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <div className="stat-value">{stats.activeCodes}</div>
            <div className="stat-label">Mã đang hoạt động</div>
          </div>
        </div>
        
        <div className="stat-card inactive">
          <div className="stat-icon">⏰</div>
          <div className="stat-content">
            <div className="stat-value">{stats.inactiveCodes}</div>
            <div className="stat-label">Mã không hoạt động</div>
          </div>
        </div>
        
        <div className="stat-card uses">
          <div className="stat-icon">🎯</div>
          <div className="stat-content">
            <div className="stat-value">{stats.totalUses}</div>
            <div className="stat-label">Tổng lượt sử dụng</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters">
        <div className="filter-group">
          <label htmlFor="code-filter">Tìm kiếm mã:</label>
          <input
            id="code-filter"
            type="text"
            placeholder="Nhập mã khuyến mãi..."
            value={codeFilter}
            onChange={(e) => setCodeFilter(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label htmlFor="type-filter">Loại giảm giá:</label>
          <select
            id="type-filter"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="">Tất cả loại</option>
            <option value="percentage">Phần trăm (%)</option>
            <option value="fixed_amount">Số tiền cố định (VND)</option>
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="status-filter">Trạng thái:</label>
          <select
            id="status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">Tất cả trạng thái</option>
            <option value="active">Đang hoạt động</option>
            <option value="inactive">Không hoạt động</option>
          </select>
        </div>

        <button onClick={() => {
          fetchSubcategories();
          fetchDiscountCodes();
        }} className="refresh-button">
          Làm mới
        </button>
      </div>

      {error && (
        <div className="error-message">
          <div>
            <strong>{error}</strong>
            <br />
            <small>💡 Mở Developer Console (F12) để xem thông tin chi tiết về lỗi</small>
          </div>
          <button 
            onClick={() => {
              setError(null);
              fetchSubcategories();
              fetchDiscountCodes();
            }}
            className="retry-button"
          >
            Thử lại
          </button>
        </div>
      )}

      {/* Discount Codes Table */}
      <div className="table-container">
        <div className="table-actions">
          <button 
            className="add-button"
            onClick={startAddingDiscount}
            disabled={isAddingDiscount}
          >
            + Thêm mã khuyến mãi
          </button>
        </div>

        {isAddingDiscount && (
          <div className="add-discount-form">
            <div className="form-row">
              <input
                type="text"
                value={newDiscount.code}
                onChange={(e) => setNewDiscount({...newDiscount, code: e.target.value})}
                onKeyDown={handleNewDiscountKeyPress}
                placeholder="Mã khuyến mãi..."
                className="form-input"
                disabled={addingDiscount}
                autoFocus
              />
              <select
                value={newDiscount.discountType}
                onChange={(e) => setNewDiscount({...newDiscount, discountType: e.target.value as 'percentage' | 'fixed_amount'})}
                onKeyDown={handleNewDiscountKeyPress}
                className="form-select"
                disabled={addingDiscount}
              >
                <option value="percentage">Phần trăm (%)</option>
                <option value="fixed_amount">Số tiền cố định (VND)</option>
              </select>
              <input
                type="number"
                value={newDiscount.discountValue}
                onChange={(e) => setNewDiscount({...newDiscount, discountValue: parseFloat(e.target.value) || 0})}
                onKeyDown={handleNewDiscountKeyPress}
                placeholder="Giá trị giảm..."
                className="form-input"
                min="0"
                step={newDiscount.discountType === 'percentage' ? '0.1' : '1000'}
                disabled={addingDiscount}
              />
              <input
                type="number"
                value={newDiscount.maxUses}
                onChange={(e) => setNewDiscount({...newDiscount, maxUses: parseInt(e.target.value) || 0})}
                onKeyDown={handleNewDiscountKeyPress}
                placeholder="Số lần tối đa..."
                className="form-input"
                min="0"
                disabled={addingDiscount}
              />
              <input
                type="datetime-local"
                value={newDiscount.expirationDate}
                onChange={(e) => setNewDiscount({...newDiscount, expirationDate: e.target.value})}
                onKeyDown={handleNewDiscountKeyPress}
                className="form-input"
                disabled={addingDiscount}
              />
              <div className="form-actions">
                <button
                  className="save-button"
                  onClick={saveNewDiscount}
                  disabled={addingDiscount}
                >
                  {addingDiscount ? 'Đang lưu...' : 'Lưu'}
                </button>
                <button
                  className="cancel-button"
                  onClick={cancelAddingDiscount}
                  disabled={addingDiscount}
                >
                  Hủy
                </button>
              </div>
            </div>
          </div>
        )}

        {filteredDiscountCodes.length === 0 ? (
          <div className="no-data-container">
            <div className="no-data">
              {discountCodes.length === 0 ? 'Không có mã khuyến mãi nào' : 'Không tìm thấy mã khuyến mãi phù hợp với bộ lọc'}
            </div>
          </div>
        ) : (
          <table className="discount-codes-table">
            <thead>
              <tr>
                <th>Mã khuyến mãi</th>
                <th>Loại giảm giá</th>
                <th>Giá trị giảm</th>
                <th>Số lần tối đa</th>
                <th>Đã sử dụng</th>
                <th>Còn lại</th>
                <th>Ngày hết hạn</th>
                <th>Ngày tạo</th>
                <th>Trạng thái</th>
              </tr>
            </thead>
            <tbody>
              {displayedDiscountCodes.map((discountCode) => (
                <tr key={discountCode.id} className={discountCode.active === 'inactive' ? 'row-inactive' : 'row-active'}>
                  <td className="discount-code">
                    {editingCode === discountCode.id && editingField === 'code' ? (
                      <div className="field-edit-container">
                        <input
                          type="text"
                          value={editingValue}
                          onChange={(e) => setEditingValue(e.target.value)}
                          onKeyDown={(e) => handleKeyPress(e, discountCode.id)}
                          onBlur={() => saveDiscountCode(discountCode.id)}
                          className="field-edit-input"
                          autoFocus
                          disabled={updatingField === discountCode.id}
                        />
                        {updatingField === discountCode.id && (
                          <span className="updating">Đang lưu...</span>
                        )}
                      </div>
                    ) : (
                      <div className="editable-container">
                        <span
                          className="field-text editable code-text clickable"
                          onClick={() => navigate(`/admin/manage-discount-codes/${discountCode.id}`)}
                          title="Click để xem chi tiết"
                        >
                          {discountCode.code}
                        </span>
                        <button
                          className="edit-icon-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            startEditing(discountCode, 'code');
                          }}
                          title="Chỉnh sửa mã"
                        >
                          ✏️
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="discount-type">
                    <span className={`type-badge ${discountCode.discountType}`}>
                      {discountCode.discountType === 'percentage' ? 'Phần trăm (%)' : 'Số tiền cố định (VND)'}
                    </span>
                  </td>
                  <td className="discount-value">
                    {editingCode === discountCode.id && editingField === 'discountValue' ? (
                      <div className="field-edit-container">
                        <input
                          type="number"
                          value={editingValue}
                          onChange={(e) => setEditingValue(e.target.value)}
                          onKeyDown={(e) => handleKeyPress(e, discountCode.id)}
                          onBlur={() => saveDiscountCode(discountCode.id)}
                          className="field-edit-input"
                          autoFocus
                          disabled={updatingField === discountCode.id}
                          min="0"
                          step={discountCode.discountType === 'percentage' ? '0.1' : '1000'}
                        />
                        {updatingField === discountCode.id && (
                          <span className="updating">Đang lưu...</span>
                        )}
                      </div>
                    ) : (
                      <div className="editable-container">
                        <span
                          className="field-text editable value-text"
                          onClick={() => startEditing(discountCode, 'discountValue')}
                          title="Click để chỉnh sửa"
                        >
                          {formatDiscountValue(discountCode.discountValue, discountCode.discountType)}
                        </span>
                        <button
                          className="edit-icon-btn"
                          onClick={() => startEditing(discountCode, 'discountValue')}
                          title="Chỉnh sửa giá trị"
                        >
                          ✏️
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="max-uses">
                    {editingCode === discountCode.id && editingField === 'maxUses' ? (
                      <div className="field-edit-container">
                        <input
                          type="number"
                          value={editingValue}
                          onChange={(e) => setEditingValue(e.target.value)}
                          onKeyDown={(e) => handleKeyPress(e, discountCode.id)}
                          onBlur={() => saveDiscountCode(discountCode.id)}
                          className="field-edit-input"
                          autoFocus
                          disabled={updatingField === discountCode.id}
                          min="0"
                          step="1"
                        />
                        {updatingField === discountCode.id && (
                          <span className="updating">Đang lưu...</span>
                        )}
                      </div>
                    ) : (
                      <div className="editable-container">
                        <span
                          className="field-text editable uses-text"
                          onClick={() => startEditing(discountCode, 'maxUses')}
                          title="Click để chỉnh sửa"
                        >
                          {discountCode.maxUses}
                        </span>
                        <button
                          className="edit-icon-btn"
                          onClick={() => startEditing(discountCode, 'maxUses')}
                          title="Chỉnh sửa số lần tối đa"
                        >
                          ✏️
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="uses-count">
                    <span className="count-text">{discountCode.usesCount}</span>
                  </td>
                  <td className="remaining-uses">
                    <span className="remaining-text">
                      {Math.max(0, discountCode.maxUses - discountCode.usesCount)}
                    </span>
                  </td>
                  <td className="expiration-date">
                    {editingCode === discountCode.id && editingField === 'expirationDate' ? (
                      <div className="field-edit-container">
                        <input
                          type="datetime-local"
                          value={editingValue}
                          onChange={(e) => setEditingValue(e.target.value)}
                          onKeyDown={(e) => handleKeyPress(e, discountCode.id)}
                          onBlur={() => saveDiscountCode(discountCode.id)}
                          className="field-edit-input"
                          autoFocus
                          disabled={updatingField === discountCode.id}
                        />
                        {updatingField === discountCode.id && (
                          <span className="updating">Đang lưu...</span>
                        )}
                      </div>
                    ) : (
                      <div className="editable-container">
                        <span
                          className={`date-text ${discountCode.active === 'inactive' ? 'inactive' : 'active'} editable`}
                          onClick={() => startEditing(discountCode, 'expirationDate')}
                          title="Click để chỉnh sửa"
                        >
                          {formatDate(discountCode.expirationDate)}
                        </span>
                        <button
                          className="edit-icon-btn"
                          onClick={() => startEditing(discountCode, 'expirationDate')}
                          title="Chỉnh sửa ngày hết hạn"
                        >
                          ✏️
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="created-date">
                    <span className="date-text">{formatDate(discountCode.createdAt)}</span>
                  </td>
                  <td className="status">
                    {editingCode === discountCode.id && editingField === 'active' ? (
                      <div className="field-edit-container">
                        <select
                          value={editingValue}
                          onChange={(e) => setEditingValue(e.target.value)}
                          onKeyDown={(e) => handleKeyPress(e, discountCode.id)}
                          onBlur={() => saveDiscountCode(discountCode.id)}
                          className="field-edit-input"
                          autoFocus
                          disabled={updatingField === discountCode.id}
                        >
                          <option value="active">Đang hoạt động</option>
                          <option value="inactive">Không hoạt động</option>
                        </select>
                        {updatingField === discountCode.id && (
                          <span className="updating">Đang lưu...</span>
                        )}
                      </div>
                    ) : (
                      <div className="editable-container">
                        <span className={`status-badge ${discountCode.active === 'inactive' ? 'inactive' : 'active'} editable`}
                          onClick={() => startEditing(discountCode, 'active')}
                          title="Click để chỉnh sửa"
                        >
                          {discountCode.active === 'inactive' ? 'Không hoạt động' : 'Đang hoạt động'}
                        </span>
                        <button
                          className="edit-icon-btn"
                          onClick={() => startEditing(discountCode, 'active')}
                          title="Chỉnh sửa trạng thái"
                        >
                          ✏️
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <div className="pagination-info">
            Hiển thị {(currentPage - 1) * recordsPerPage + 1} - {Math.min(currentPage * recordsPerPage, memoFilteredDiscountCodes.length)} của {memoFilteredDiscountCodes.length} kết quả
          </div>
          <div className="pagination-controls">
            <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="pagination-btn">««</button>
            <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} className="pagination-btn">«</button>
            {(() => {
              const buttons = [] as React.ReactElement[];
              const start = Math.max(1, currentPage - 2);
              const end = Math.min(totalPages, currentPage + 2);
              for (let i = start; i <= end; i++) {
                buttons.push(
                  <button key={i} onClick={() => setCurrentPage(i)} disabled={i === currentPage} className={`pagination-btn ${i === currentPage ? 'active' : ''}`}>{i}</button>
                );
              }
              return buttons;
            })()}
            <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} className="pagination-btn">»</button>
            <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="pagination-btn">»»</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageDiscountCodes; 